import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface QuoteCheck {
  id: string;
  trade: string;
  job_description: string;
  quoted_amount: number;
  contractor_name: string | null;
  status: string;
  verdict: string | null;
  fair_price_low: number | null;
  fair_price_high: number | null;
  admin_response: string | null;
  created_at: string;
}

const statusTone: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  reviewing: "bg-blue-100 text-blue-800",
  answered: "bg-green-100 text-green-800",
};

export function QuoteCheckSection() {
  const [checks, setChecks] = useState<QuoteCheck[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [analysingId, setAnalysingId] = useState<string | null>(null);

  const [form, setForm] = useState({
    trade: "",
    job_description: "",
    quoted_amount: "",
    contractor_name: "",
    location: "",
  });
  const { toast } = useToast();

  const load = async () => {
    const { data, error } = await supabase
      .from("quote_checks")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Quote check load failed:", error.message);
      return;
    }
    setChecks((data as QuoteCheck[]) ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = Number(form.quoted_amount);
    if (!form.trade.trim() || !form.job_description.trim() || !Number.isFinite(amount) || amount <= 0) {
      toast({
        title: "Check your details",
        description: "Trade, description and a valid quoted amount are required.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error("Not signed in");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", auth.user.id)
        .maybeSingle();

      const { data: inserted, error } = await supabase
        .from("quote_checks")
        .insert({
          user_id: auth.user.id,
          profile_id: profile?.id ?? null,
          trade: form.trade.trim(),
          job_description: form.job_description.trim(),
          quoted_amount: amount,
          contractor_name: form.contractor_name.trim() || null,
          location: form.location.trim() || null,
        })
        .select("id")
        .single();
      if (error) throw error;

      setForm({ trade: "", job_description: "", quoted_amount: "", contractor_name: "", location: "" });
      setAnalysingId(inserted.id);
      load();

      const { error: fnError } = await supabase.functions.invoke("analyze-quote-check", {
        body: { quote_check_id: inserted.id },
      });
      if (fnError) {
        toast({
          title: "Quote saved, assessment delayed",
          description: "We'll finish checking this shortly.",
        });
      } else {
        toast({
          title: "Quote checked",
          description: "Your instant assessment is ready below.",
        });
      }
      setAnalysingId(null);
      load();

    } catch (error: any) {
      console.error("Quote check submit failed:", error);
      toast({
        title: "Could not submit",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Is this builder overcharging me?</CardTitle>
          <CardDescription>
            Send us a quote and you'll get an instant assessment of whether the price is fair for the
            work and for prices in your area.
          </CardDescription>

        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="qc-trade">Trade</Label>
                <Input
                  id="qc-trade"
                  value={form.trade}
                  onChange={(e) => setForm({ ...form, trade: e.target.value })}
                  placeholder="e.g. Plumbing"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qc-amount">Quoted amount (£)</Label>
                <Input
                  id="qc-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.quoted_amount}
                  onChange={(e) => setForm({ ...form, quoted_amount: e.target.value })}
                  placeholder="450.00"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qc-contractor">Contractor name (optional)</Label>
                <Input
                  id="qc-contractor"
                  value={form.contractor_name}
                  onChange={(e) => setForm({ ...form, contractor_name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qc-location">Location (optional)</Label>
                <Input
                  id="qc-location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Town or postcode"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="qc-description">What's the job?</Label>
              <Textarea
                id="qc-description"
                rows={4}
                value={form.job_description}
                onChange={(e) => setForm({ ...form, job_description: e.target.value })}
                placeholder="Describe the work quoted for, including materials if you know them."
              />
            </div>
            <Button type="submit" disabled={submitting}>
              {submitting ? (analysingId ? "Checking your quote…" : "Sending…") : "Check my quote"}
            </Button>

          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="font-semibold">Your quote checks</h3>
        {checks.length === 0 ? (
          <p className="text-sm text-muted-foreground">You haven't submitted a quote yet.</p>
        ) : (
          checks.map((check) => (
            <Card key={check.id} className="border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">
                    {check.trade} — £{Number(check.quoted_amount).toFixed(2)}
                  </CardTitle>
                  <Badge className={statusTone[check.status] ?? ""} variant="secondary">
                    {analysingId === check.id ? "checking…" : check.status}
                  </Badge>

                </div>
                <CardDescription>{check.job_description}</CardDescription>
              </CardHeader>
              {(check.admin_response || check.verdict) && (
                <CardContent className="space-y-2 text-sm">
                  {check.verdict && (
                    <p className="font-medium">Verdict: {check.verdict}</p>
                  )}
                  {check.fair_price_low != null && check.fair_price_high != null && (
                    <p className="text-muted-foreground">
                      Typical fair range: £{Number(check.fair_price_low).toFixed(2)} – £
                      {Number(check.fair_price_high).toFixed(2)}
                    </p>
                  )}
                  {check.admin_response && <p>{check.admin_response}</p>}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
